<template>
  <div class="resource">
    <div class="resource-inner" style="padding-bottom:40px;">
      <div>Documentation</div>
      <div></div>
      <div class="resource-title">I. Introduction</div>
      <div class="resource-title2">A. What is PLncDB (Plant Long noncoding RNA Database)?</div>
      <div class="document_content">
        <!-- PLncDB (Plant Long noncoding RNA Database) is a comprehensive functional plant lncRNA database. For species with whole genome sequences and deeply sequenced RNA libraries, we employed a suite of uniform methods and collected extensive knowledge on lncRNAs from other databases, including fundamental annotation on sequences, validated method, expression and target genes.<br /> In current version 1.2, we have registered 46 species phylogenetically ranging from chlorophytes to angiosperms, 20338 lncRNAs and 141327 predicted lncRNA-target pairs. Meanwhile, 15437 transcriptome datasets derived from various samples including different tissues, developmental stages, mutants and stress treatments were used in quantification of lncRNA expression patterns. Moreover, 456 epigenetic modification datasets and small RNA datasets were also integrated to explore the epigenetic regulation of lncRNAs.<br /> These unique features such as annotating lncRNAs with an uniform method, solid and extensive information on lncRNA expression, references, and providing many convenient data accesses, powerful search and download engines, have made PLncDB a truly functional lncRNA knowledgebase. We believe PLncDB will provide novel insights into plant lncRNA research and benefit the whole research community. -->
        PLncDB (Plant Long noncoding RNA Database) is a comprehensive functional plant lncRNA database. For species with whole genome
        sequences and deeply sequenced RNA libraries, we employed a suite of uniform methods and collected extensive knowledge
        on lncRNAs from other databases, including fundamental annotation on sequences, validated method, expression and
        target genes. In current version 2.0, we have registered {{bottomObj.species}} species phylogenetically ranging from
        chlorophytes to embryophytes, {{bottomObj.lncRNAs}} lncRNAs and {{bottomObj.lncRNA_expression}} predicted lncRNA-target
        pairs. Meanwhile, {{bottomObj.Run_IDs}} transcriptome datasets derived from various samples including different tissues,
        developmental stages, mutants and stress treatments were used in quantification of lncRNA expression patterns. Moreover,
        456 epigenetic modification datasets and small RNA datasets were also integrated to explore the epigenetic regulation
        of lncRNAs. These unique features such as annotating lncRNAs with an uniform method, solid and extensive information
        on lncRNA expression, references, and providing many convenient data accesses, powerful search and download engines,
        have made PLncDB a truly functional lncRNA knowledgebase. We believe PLncDB will provide novel insights into plant
        lncRNA research and benefit the whole research community.
      </div>
      <div class="resource-title">II. Datasets and Workflow</div>
      <div class="resource-title2">A. Datasets</div>
      <div class="document_content">
        <!-- Original Datasets of RNA-Seq data, lncRNA-seq data, Chip-seq data and sRNA-seq data were derived from NCBI, and genome sequences, annotation files (transcript, GFF3, cDNA) were download from NCBI or Ensembl. Experimental validated lncRNAs were downloaded from EVLncRNAs database. Totally, 46 plant genome reference sequences and related annotation information, 13678 RNA-seq datasets, 156 sRNA-Seq datasets, 116 Chip-Seq datasets were downloaded. As a result, 20338 lncRNAs and 141327 predicted lncRNA-target pairs were achieved through a suite of uniform methods (details as follows in Workflow). -->
        Original Datasets of RNA-Seq data, lncRNA-seq data, Chip-seq data and sRNA-seq data were derived from NCBI, and genome sequences,
        annotation files (transcript, GFF3, cDNA) were download from NCBI or Ensembl. Experimental validated lncRNAs were
        downloaded from EVLncRNAs database. Well known lncRNA annotation from RNAcenter were also integrated to PLncDB. Totally,
        {{bottomObj.species}} plant genome reference sequences and related annotation information, {{bottomObj.Run_IDs}}
        RNA-seq datasets, 456 Chip-Seq datasets were downloaded. As a result, {{bottomObj.lncRNAs}} lncRNAs and {{bottomObj.lncRNA_expression}}
        predicted lncRNA-target pairs were achieved through a suite of uniform methods (details as follows in Workflow).
      </div>
      <div class="resource-title2">B. Workflow</div>
      <div class="resource-title3">Data pre-processing</div>
      <div class="document_content">
        <!-- The format of RNA-seq, sRNA-Seq or Chip-Seq datasets from GEO DataSets of NCBI is not uniform. The majority of these datasets keep original Fastq format with/without adapter sequences while small number of them have been transformed into Fasta format. Linux version 2.8.2 of SRA toolkit was employed to convert original compressed files into Fastq format. Trim Galore was used to trim adapters. To format sRNA-Seq datasets, trimmed Fastq files are further compacted to Fasta format with collapsed identical reads by an in-house Perl script. -->
        The format of RNA-seq, sRNA-Seq or Chip-Seq datasets from GEO DataSets of NCBI is not uniform. The majority of these datasets
        keep original Fastq format with/without adapter sequences while small number of them have been transformed into Fasta
        format. Linux version 2.8.2 of SRA toolkit was employed to convert original compressed files into Fastq format. Trim
        Galore was used to trim adapters.
      </div>
      <div class="resource-title3">Annotation of lncRNA</div>
      <div class="document_content">
        <!-- Clean data were aligned to the reference genome using the read aligner HIAST2 . The transcriptome of each experiment was assembled separately using stringti , all gtf result files were merged into one with stringtie --merge. Then, we compared the assembled transcript isoforms with the genome reference annotation information, which represents all protein coding gene models identified by tobacco genome project. Transcripts with a length shorter than 200bp and an open reading frame (ORF) length longer than 120 aa were discarded, in order to remove transcripts that may encode short protein, swiss-prot database were searched using blastx program with the parameter -e 1.0e-4 -S 1. The CPC and PLEK programs were used to calculate the coding potential of the remaining transcripts. Only transcripts with both CPC and PLEK scores less than 0 were used for the subsequent analysis. The remaining transcripts located in intergenic regions were identified as lincRNA candidates. If the transcripts were transcribed from the antisense strands of known genes, they will be considered as NATs candidates. For the transcripts located in intron region of known genes, they were identified as incRNA candidates. -->
        Clean data were aligned to the reference genome using the read aligner HIAST2 . The transcriptome of each experiment was
        assembled separately using stringtie, all gtf result files were merged into one with stringtie --merge. Then, we
        compared the assembled transcript isoforms with the genome reference annotation information, which represents all
        protein coding gene models. Transcripts with a length shorter than 200bp and an open reading frame (ORF) length longer
        than 120 aa were discarded. In order to remove transcripts that may encode short protein, swiss-prot database were
        searched using blastx program with the parameter -e 1.0e-4 -S 1. The CPC and PLEK programs were used to calculate
        the coding potential of the remaining transcripts. Only transcripts with both CPC and PLEK scores less than 0 were
        considered as lncRNA candidate.
      </div>
      <div class="document_img"></div>
      <div class="resource-title3">Expression analysis</div>
      <div class="document_content">
        <!-- Similar with gene expression, TPM (Transcripts Per Kilobase Million) was used to estimate the abundance of assembled transcripts and to measure the expression level of lncRNAs. -->
        Similar with gene expression, TPM (Transcripts Per Kilobase Million) was used to estimate the abundance of assembled transcripts
        and to measure the expression level of lncRNAs. Target prediction and validation All lncRNAs candidates were used
        to predict miRNA mimic target using psMimic software. WGCNA was used to analyze the coexpression of lncRNAs and their
        potential targets.
      </div>
      <!-- <div class="resource-title3">Target prediction and validation</div>
      <div class="document_content">
        All lncRNAs candidates were used to predict miRNA mimic target using psRobot_mim software. WGCNA was used to analyze the coexpression of lncRNAs and their target protein-coding genes
      </div> -->
      <div class="resource-title">III. FAQ</div>
      <div class="resource-title2">A. What information does PLncDB provide for plant lncRNAs?</div>
      <div class="document_content">
        PLncDB attempts to provide the following functions related to long noncoding RNAs (lncRNAs):<br /> (1) {{bottomObj.species}}
        species with sequenced whole genomes and available public RNA-Seq data;<br /> (2) Genomic information for a large
        number of lncRNAs collected from various resources; <br /> (3) The basic information of lncRNAs, including genome
        location, sequences, secondary structure, etc., in the lncRNA detailed information page;<br /> (4) Integration of
        transcriptome datasets derived from various samples including different tissues, developmental stages, mutants and
        stress treatments; <br /> (5) A list of epigenetic modification datasets and small RNA datasets.<br /> (6) Validated
        lncRNA-target pairs and potential targets for lncRNAs based on expression pattern.<br />
      </div>
      <div class="resource-title2">B. How can I get detailed lncRNA information for specific lncRNA of interest?</div>
      <div class="document_content">
        <!-- PLncDB applies a quickly search in the top-right corner of web page. Enter lncRNA locus ID, lncRNA locus location or species name and the search engine will return all hits with statistic result in a table. Then click on the lncRNA locus accession, you can find the detail information of the lncRNA you are interested.<br /> Alternatively, you can also search lncRNA in Search Page with more convenient and specific search engines, Eight powerful search engines were deployed, and specifically, you can search by keywords, sequence (blastn was used), genome location and expression. Click "Search". You'll quickly receive a summary of results to meet your search need. -->
        PLncDB applies a quickly search in the top-right corner of web page. Enter lncRNA locus ID, lncRNA locus location or species
        name and the search engine will return all hits with statistic result in a table. Then click on the lncRNA locus
        accession, you can find the detail information of the lncRNA you are interested. Alternatively, you can also search
        lncRNA in Search Page with more convenient and specific search engines. Five powerful search engines were deployed,
        and specifically, you can search by keywords, sequence (blastn was used), genome location, expression or targets.
        Click "Search". You'll quickly receive a summary of results to meet your search need.
      </div>
      <div class="resource-title2">C. How can I get lncRNA for any given species?</div>
      <div class="resource-title3">We recommend two ways to get the lncRNAs in a species. </div>
      <div class="document_content">
        <!-- Firstly, you can click into Browse Page, 46 available species in our database ordered by taxon. Once you've chosen a species (by single-clicking its scientific name), you'll see a popup which contains the summary information, including genome information, number of lncRNAs and RNA-Seq datasets. Click on lncRNA locus, it will open a new page which contains a table of lncRNAs belongs to this species. You can operate the checkbox to download the information of lncRNAs you interest or download all by click the button 'Download all'.<br /> Secondary, the access to data files for each PLncDB organism is available via the Download Page. Link to the FTP is provided by the Download Page in the top-left, and the data files are stored in the folder of each organism. For convenience, we create a user-defined download manner. You can download for lncRNAs detailed information by a customized manner. -->
        Firstly, you can click into Browse Page, {{bottomObj.species}} available species in our database ordered by taxon. Once you've
        chosen a species (by single-clicking its scientific name), you'll see a popup which contains the summary information,
        including genome information, number of lncRNAs and RNA-Seq datasets. Click on lncRNA locus, it will open a new page
        which contains a table of lncRNAs belongs to this species. You can operate the checkbox to download the information
        of lncRNAs you interest or download all by click the button 'Download all'. Secondary, the access to data files for
        each PLncDB organism is available via the Download Page. Link to the FTP is provided by the Download Page in the
        top-left, and the data files are stored in the folder of each organism. For convenience, we create a user-defined
        download manner. You can download for lncRNAs detailed information by a customized manner.
      </div>
      <div class="resource-title2">D. How to download the data in PLncDB?</div>
      <div class="document_content">
        All data in PLncDB are access to download in Download Page. <br /> The data in PLncDB can be downloaded from the
        FTP server in a variety of formats. To facilitate storage and download, all data in a species are stored in one folder.<br
        /> User-defined download is a convenient access to download the data in a customized manner. Downloaded files are
        compressed into a zip file.
      </div>
      <div class="resource-title">IV. Support</div>
      <div class="resource-title2">Submit your data on PLncDB!</div>
      <div class="document_content">
        If you have a group of novel lncRNAs in plants, and would like to submit them to PLncDB. Please go to Submit Page.
      </div>
      <div class="resource-title">V. Citation</div>
      <div class="document_content">
        Data files contained in the PLncDB are free of all copyright restrictions and made fully and freely available for non-commercial
        use. Users of the data should cite the following articles:<br /> Jin JJ, Lu P, Xu YL, Li ZF, Yu SZ,Liu J,
        Wang H, Chua NH, Cao PJ. PLncDB V2.0: a comprehensive encyclopedia of plant long noncoding RNAs. Submitted.<br />        Jin JJ, Liu J, Wang H, Wong L, Chua NH. PLncDB: plant long non-coding RNA database. Bioinformatics 2013 APR 15; 29(8):1068-1071.
      </div>
    </div>
    <!-- <div class="resource-inner">
      <div>Resource</div>
      <div></div>
      <div class="resource-title">PlncDB database</div>
      <div class="resource-content">
        <ul>
          <li>Species</li>
          <li>LncRANs </li>
          <li>Genes </li>
          <li>Sequences</li>
          <li>RNA-Seq</li>
        </ul>
        <ul>
          <li>{{bottomObj.species}}</li>
          <li>{{bottomObj.lncRNAs}}</li>
          <li>{{bottomObj.Genes}}</li>
          <li>{{bottomObj.Sequences}}</li>
          <li>{{bottomObj.Run_IDs}}</li>
        </ul>
      </div>
    </div> -->
  </div>
</template>
<script>
export default {
  name: "Map",
  data() {
    return {
      bottomObj: {
        Genes: '',
        Run_IDs: '',
        Sequences: '',
        expression: '',
        lncRNAs: '',
        species: ''
      }
    };
  },
  mounted() {
    this.bottomObj = JSON.parse(sessionStorage.getItem("specie_count"))
  },
  methods: {
  }
};
</script>

<style scoped>
.resource {
  width: 100%;
  padding: 30px 0px;
  background: #f5f9fc;
}
.resource .resource-inner {
  width: 1200px;
  padding: 0px 20px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 1);
}
.resource .resource-inner div:first-child {
  height: 50px;
  font-size: 30px;
  font-family: PingFang SC;
  font-weight: bold;
  color: rgba(51, 51, 51, 1);
  line-height: 50px;
}
.resource .resource-inner div:nth-child(2) {
  width: 100%;
  height: 1px;
  background: rgba(229, 233, 235, 1);
}
.resource .resource-inner .resource-title {
  margin-top: 40px;
  height: 28px;
  font-size: 24px;
  font-family: PingFang SC;
  font-weight: bold;
  line-height: 28px;
}
.resource .resource-inner .resource-content {
  width: 100%;
  border-bottom: 1px solid rgba(139, 182, 68, 1);
}
.resource .resource-inner .resource-content:last-child {
  width: 100%;
  border-bottom: none;
}

.resource .resource-inner .resource-content ul {
  display: inline-block;
  height: 100%;
  list-style: none;
  margin: 20px 0;
  padding: 0;
  margin-top: 20px;
}
.resource .resource-inner .resource-content ul:first-child {
  width: 20%;
}
.resource .resource-inner .resource-content ul:last-child {
  width: 80%;
}
.resource .resource-inner .resource-content ul:first-child li {
  height: 40px;
  font-size: 20px;
  font-family: PingFang SC;
  font-weight: 400;
  color: rgba(51, 51, 51, 1);
  line-height: 40px;
}
.resource .resource-inner .resource-content ul:last-child li {
  height: 40px;
  font-size: 16px;
  font-family: PingFang SC;
  font-weight: 400;
  color: rgba(102, 102, 102, 1);
  line-height: 40px;
}
/* ul li:hover {
  color: rgba(139, 182, 68, 1) !important;
} */
.resource-title2 {
  width: 100%;
  height: 22px;
  font-size: 20px;
  font-family: PingFang SC;
  font-weight: 500;
  color: rgba(51, 51, 51, 1);
  line-height: 28px;
  margin: 15px 0;
}
.document_content {
  width: 100%;
  height: auto;
  font-size: 16px;
  font-family: PingFang SC;
  font-weight: 400;
  color: rgba(102, 102, 102, 1);
  line-height: 28px;
}
.resource-title3 {
  width: 100%;
  height: 20px;
  font-size: 20px;
  font-family: PingFang SC;
  font-weight: 500;
  color: rgba(51, 51, 51, 1);
  line-height: 28px;
  margin: 17px 0;
}
.document_img {
  width: 100%;
  height: 700px;
  margin: 0 auto;
  background: url(~@/assets/img/documentation.svg) no-repeat center;
  background-size: 80%;
}
</style>
